import { useTranslation } from "react-i18next";
import { MapPin, ShieldCheck, Clock } from "lucide-react";
import heroVehicles from "@/assets/frostflotte-hero-blau-vito-sprinter.jpg";
import FadeIn from "./FadeIn";
import HeroBookingBar from "./HeroBookingBar";

const ACCENT_LIGHT = "hsl(195 80% 72%)";

const Hero = () => {
  const { t } = useTranslation();

  const trustLine = [
    { Icon: ShieldCheck, label: t("hero.trustLine.atp") },
    { Icon: Clock, label: t("hero.trustLine.response") },
    { Icon: MapPin, label: t("hero.trustLine.pickup") },
  ];

  return (
    <section id="hero" className="relative bg-background">
      <div className="relative bg-[hsl(var(--hero-panorama-bg))] pt-[76px] pb-6 lg:pt-[88px] lg:pb-6">
        <div className="container-site relative">
          <div className="grid items-center gap-3 md:grid-cols-[45%_55%] md:gap-5 lg:min-h-[330px] lg:grid-cols-[42%_58%] lg:gap-3 xl:min-h-[360px] 2xl:min-h-[400px]">
            {/* LEFT — Text */}
            <div className="relative z-[2] flex min-w-0 max-w-[520px] flex-col pt-2 lg:self-center">
              <FadeIn delay={0}>
                <span
                  className="inline-flex items-center gap-2 text-[11px] lg:text-xs font-bold uppercase"
                  style={{ color: ACCENT_LIGHT, letterSpacing: "0.14em" }}
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
                  {t("hero.eyebrow")}
                </span>
              </FadeIn>

              <FadeIn delay={60}>
                <h1
                  className="mt-3 m-0 font-display font-extrabold text-primary-foreground [&_accent]:text-[hsl(195,80%,72%)]"
                  style={{
                    fontSize: "clamp(1.75rem, 3.15vw, 2.75rem)",
                    lineHeight: 1.08,
                    letterSpacing: "0",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: t("hero.title").replace(
                      /<accent>(.*?)<\/accent>/g,
                      `<span style="color:${ACCENT_LIGHT}">$1</span>`,
                    ),
                  }}
                />
              </FadeIn>

              <FadeIn delay={120}>
                <p className="mt-4 max-w-[420px] text-sm leading-relaxed text-primary-foreground/85 lg:text-base">
                  {t("hero.subtitle")}
                </p>
              </FadeIn>
            </div>

            {/* RIGHT — freigestellte Fahrzeuge ohne Bildkarte */}
            <div className="relative z-[1] h-[220px] w-full overflow-hidden sm:h-[280px] md:h-[300px] md:self-center lg:-ml-[5%] lg:h-[360px] lg:w-[105%] lg:justify-self-end xl:h-[400px]">
              <FadeIn delay={140} className="h-full">
                <div
                  role="img"
                  aria-label={t("hero.imageAlt")}
                  className="h-full w-full bg-cover bg-right bg-no-repeat md:max-lg:[background-size:120%_auto] md:max-lg:[background-position:right_center]"
                  style={{ backgroundImage: `url(${heroVehicles})` }}
                />
              </FadeIn>
            </div>
          </div>

          <FadeIn delay={240} className="relative z-10 mt-4 lg:mt-0">
            <div
              className="rounded-lg bg-card p-4 lg:p-5"
              style={{
                boxShadow:
                  "0 12px 28px -18px hsl(218 78% 10% / 0.35), 0 2px 5px hsl(218 78% 10% / 0.12)",
              }}
            >
              <HeroBookingBar />
            </div>
          </FadeIn>
        </div>
      </div>

      <div className="bg-secondary/35">
        <div className="container-site py-4">
        <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          {trustLine.map(({ Icon, label }) => (
            <li key={label} className="inline-flex items-center gap-2">
              <Icon className="h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
              {label}
            </li>
          ))}
        </ul>
        </div>
      </div>
    </section>
  );
};

export default Hero;
